import { ShareButton } from "components/share-button/share-button";
import { GetServerSideProps } from "next";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import GuruBimbelColearnBanner from "public/assets/tanya/guru-bimbel-colearn-banner.png";
import React, { useRef } from "react";
import trackEvent from "services/mixpanel/mixpanel";
import {
  VideoDetailBannerLink,
  VideoDetailHeaderCard,
  VideoDetailStatsContainer,
  VideoDetailTagContainer,
  VideoDetailTagItem,
  VideoDetailTagLink,
  VideoDetailTitle,
  VideoDetailsBanner,
  VideoDetailsBannerContainer,
  VideoDetailsContainer,
  VideoDetailsPlayerContainer,
  VideoDetailsPlayerWrapper
} from "styles/pages/tanya-video-detail.styles";

import { Accordion } from "components/accordion/accordion";
import { constants } from "constants/constants";
import { TanyaQuestionIdSEO } from "seo/tanya-questionid.seo";
import { getVideoData } from "services/api";
import { VideoAPIData } from "services/api/api.types";
import { tanyaEventNames } from "services/mixpanel/tanya-events";

import {
  BreadCrumbItem,
  BreadCrumbLink,
  BreadCrumbsContainer
} from "styles/common/breadcrumb";
import {
  TanyaSearchBarContainer,
  TanyaVideoPageContainer
} from "styles/pages/tanya-search.styles";
import { videoPageTrackingData } from "utils/pages/utils";
import { getGradeGroup, getQueryValue, getSubjectInBahasa } from "utils/utils";
import {
  BreadCrumbDataParams,
  getBreadCrumbDetails,
  getVideoMakerDetails
} from "../../../utils/tanya-utils";

import { IVideoMakerData } from "components/video-maker-block/video-maker-block.types";

import dynamic from "next/dynamic";

import { TanyaVideoPlayer } from "components/tanya-video-player/tanya-video-player";
import ReactPlayer from "react-player";

const SearchBar = dynamic(() => import("components/search-bar/search-bar"));

const TanyaSimilarQuestions = dynamic(
  () => import("components/tanya-similar-question/tanya-similar-question")
);

const SubjectTopicsGrid = dynamic(
  () => import("components/subject-topic-grid/subject-topic-grid")
);

const getThumbNailURL = (
  thumbNailURLs: string[] | undefined,
  coverImage: string
) =>
  thumbNailURLs && thumbNailURLs.length > 0 ? thumbNailURLs[0] : coverImage;

const VideoDetail: React.FC<{
  videoDetailsData: VideoAPIData;
  videoMakerDetails: IVideoMakerData;
}> = ({ videoDetailsData, videoMakerDetails }) => {
  const {
    subjects,
    title,
    sections,
    grades,
    id,
    chapters,
    topics,
    cover_image,
    video_url,
    slug,
    slugs,
    upload_date,
    // upvote_count = 0,
    duration,
    transcript,
    curriculum,
    thumbnail_urls,
    youtube_url,
    similar_questions
  } = videoDetailsData;

  const videoPlayerHandleRef = useRef<ReactPlayer>(null);

  const videoURL = `/tanya/${id}/${slug}`;
  const shareURL = `${process.env.NEXT_PUBLIC_SITE_URL}${videoURL}`;

  const router = useRouter();
  const { utm_source, utm_medium, sharedBy } = router.query;

  const breadCrumbParams: BreadCrumbDataParams = {
    subject: subjects[0],
    subjectSlug: slugs.subjects[0],
    topic: topics[0],
    topicSlug: slugs.topics[0],
    section: sections[0],
    sectionSlug: slugs.sections[0],
    chapter: chapters[0],
    chapterSlug: slugs.chapters[0],
    grade: grades[0],
    gradeSlug: slugs.grades[0],
    gradeGroup: getGradeGroup(grades[0])
  };

  const [breadCrumbData, breadCrumbSchema] =
    getBreadCrumbDetails(breadCrumbParams);

  const handleViewMoreVideosClick = () => {
    router.push(constants.TANYA_ALL_QUESTION_PAGES);
  };
  return (
    <>
      <div>
        <TanyaVideoPageContainer>
          <TanyaQuestionIdSEO
            title={title}
            subject={subjects[0]}
            grade={grades[0]}
            section={sections[0]}
            topic={topics[0]}
            thumbnailURL={getThumbNailURL(thumbnail_urls, cover_image)}
            video_url={youtube_url ? youtube_url : video_url}
            id={id}
            slug={slug}
            upload_date={upload_date}
            // upvote_count={upvote_count}
            duration={duration}
            transcript={transcript}
            curriculum={
              curriculum && curriculum.length > 0 ? curriculum[0] : null
            }
            videoMakerData={videoMakerDetails}
          />
          <TanyaSearchBarContainer>
            <SearchBar isFullWidth={true} />
          </TanyaSearchBarContainer>

          <VideoDetailsPlayerWrapper>
            <VideoDetailsPlayerContainer>
              {videoDetailsData.video_url && (
                <TanyaVideoPlayer
                  videoData={videoDetailsData}
                  playerRef={videoPlayerHandleRef}
                />
              )}
            </VideoDetailsPlayerContainer>
          </VideoDetailsPlayerWrapper>
          <VideoDetailsContainer>
            <VideoDetailHeaderCard>
              <BreadCrumbsContainer>
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: JSON.stringify(breadCrumbSchema)
                  }}
                />
                {breadCrumbData.map((data, index) => (
                  <BreadCrumbItem
                    key={`breadcrumb-item-${index}`}
                    data-testid={`breadcrumb-item-${index}`}
                  >
                    <BreadCrumbLink href={data.url}>{data.text}</BreadCrumbLink>
                  </BreadCrumbItem>
                ))}
              </BreadCrumbsContainer>
              <VideoDetailTitle>Video solusi : {title}</VideoDetailTitle>
              <VideoDetailTagContainer>
                {topics.map((topic, idx) => (
                  <VideoDetailTagItem
                    key={`video-tag-item-${topic}`}
                    data-testid={`video-tag-topic-item-${topic}`}
                  >
                    <VideoDetailTagLink
                      href={`/tanya/topic/${grades[0]}/${getSubjectInBahasa(
                        subjects[0]
                      )}/${slugs?.topics[idx]}`}
                    >
                      {topic}
                    </VideoDetailTagLink>
                  </VideoDetailTagItem>
                ))}
                {chapters.map((chapter, idx) => (
                  <VideoDetailTagItem
                    key={`video-tag-item-${chapter}`}
                    data-testid={`video-tag-chapter-item-${chapter}`}
                  >
                    <VideoDetailTagLink
                      href={`/tanya/chapter/${grades[0]}/${getSubjectInBahasa(
                        subjects[0]
                      )}/${slugs?.chapters[idx]}`}
                    >
                      {chapter}
                    </VideoDetailTagLink>
                  </VideoDetailTagItem>
                ))}
                {sections.map((section, idx) => (
                  <VideoDetailTagItem
                    key={`video-card-section-${section}`}
                    data-testid={`video-tag-section-item-${section}`}
                  >
                    <VideoDetailTagLink
                      href={`/tanya/section/${grades[0]}/${getSubjectInBahasa(
                        subjects[0]
                      )}/${slugs?.sections[idx]}`}
                    >
                      {section}
                    </VideoDetailTagLink>
                  </VideoDetailTagItem>
                ))}
                {subjects.map((subject) => (
                  <VideoDetailTagItem
                    key={`video-tag-item-${subject}`}
                    data-testid={`video-tag-subject-item-${subject}`}
                  >
                    <VideoDetailTagLink as="div" isNotClickable>
                      {getSubjectInBahasa(subject)}
                    </VideoDetailTagLink>
                  </VideoDetailTagItem>
                ))}
              </VideoDetailTagContainer>
              <VideoDetailStatsContainer>
                <ShareButton shareURL={shareURL} />
              </VideoDetailStatsContainer>

              {similar_questions && (
                <TanyaSimilarQuestions
                  similarQuestions={similar_questions}
                  topicName={topics[0]}
                  handleViewMore={handleViewMoreVideosClick}
                />
              )}
            </VideoDetailHeaderCard>

            <VideoDetailsBannerContainer>
              <VideoDetailsBanner>
                <Link
                  href={
                    "https://colearn.id/bimbel?utm_source=colearn_web&utm_term=tanya_video_banner"
                  }
                  passHref
                >
                  <VideoDetailBannerLink data-testid="bimbel-banner-internal-ad">
                    <Image
                      onClick={() => {
                        trackEvent({
                          eventName: tanyaEventNames.liveClassBannerClicked,
                          payload: videoPageTrackingData({
                            videoData: videoDetailsData,
                            utm_source,
                            utm_medium,
                            sharedBy
                          })
                        });
                      }}
                      src={GuruBimbelColearnBanner}
                      alt="Join live classes at Colearn"
                    />
                  </VideoDetailBannerLink>
                </Link>
              </VideoDetailsBanner>
            </VideoDetailsBannerContainer>

            {transcript && (
              <div id="answer" data-testid="tanya-video-transcript">
                <Accordion
                  isBordered
                  title={"Teks video"}
                  content={transcript}
                />
              </div>
            )}
          </VideoDetailsContainer>
          <SubjectTopicsGrid />
        </TanyaVideoPageContainer>
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { params, req, res } = context;

  const reqHeaders = {
    "X-Forwarded-For": req.headers["x-forwarded-for"]
  };

  const questionUUID: string | null = getQueryValue(params?.questionId);
  const searchTerm = params?.slug || "";

  if (!questionUUID) {
    return {
      notFound: true
    };
  }
  const [videoDataResult] = await Promise.allSettled([
    getVideoData(questionUUID, reqHeaders)
  ]);

  if (
    videoDataResult.status === "rejected" ||
    videoDataResult.value.slug !== searchTerm
  ) {
    return { notFound: true };
  }

  let videoMakerDetails = null;

  if (
    videoDataResult.value.part_of_experiment &&
    videoDataResult.value.part_of_experiment === "vsm_bio_1"
  ) {
    videoMakerDetails = getVideoMakerDetails(
      videoDataResult.value.subjects[0],
      videoDataResult.value.grades[0]
    );
  }
  videoDataResult.value.similar_questions =
    videoDataResult.value.similar_questions?.filter((ques) => !!ques);

  res.setHeader("Cache-Control", "public, s-maxage=604800");

  return {
    props: {
      videoDetailsData: videoDataResult.value,
      videoMakerDetails
    }
  };
};

export default VideoDetail;
